/**
 * src/components/pdf/2024/sections/EmpoweringPeoplePDF.js
 *
 * Empowering People section — section cover + content pages.
 *
 * Named exports: EmpoweringPeoplePage, EmpoweringPeopleSection
 * Default export: standalone Document (used by the generate script)
 */

import React from 'react';
import { Document, View, Text, Image, Link } from '@react-pdf/renderer';
import { styles, COLORS, registerFonts } from '../styles';
import PageTemplate from '../PageTemplate';
import {
  H1, H2, H3, H4, H5, Body, Small, Section,
  BulletList, Blockquote, SpotlightBox,
  SectionCover,
  PageOffsetContext,
} from '../shared';

// Empowering People hero (also used for the section cover)
import empoweringPeopleHero from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/empowering_people_hero.jpg';
import myWellbeingImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/my_wellbeing.jpg';
import club1938Img from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/club-1938_1.jpg';
import vietnamSportsDayImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/vietnam_spotlight_sport-day_1.jpg';
import aquaTowerImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/aqua_tower.jpg';
import raiseAglassImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/planet_water_glasses.jpg';
import volunteerImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/Employee_Volunteer.jpg';
import volunteer2Img from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/Employee_Volunteer_2.jpeg';
import leaveNoTraceImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/leave_no_trace_1.jpg';
import leaveNoTrace2Img from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/leave_no_trace_2.jpg';

// ERN logos
import vamosUnidosLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/vamosUnidos_logo.png';
import wliLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/wli_logo.png';
import benLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/ben_logo.png';
import prideLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/pride_logo.png';
import capeLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/cape_logo.png';
import bravoLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/bravo_logo.png';
import ourTrailLogo from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/ern-logos/ourTrail_logo.png';

// Workforce tables as images
import workforceLevelGenderImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/csc_workforce_level_gender.png';
import workforceLevelEthnicityImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/csc_workforce_level_ethnicity.png';
import workforceRoleGenderImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/csc_workforce_role_gender.png';
import workforceRoleEthnicityImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/csc_workforce_role_ethnicity.png';
import workforceRegionImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/empowering-people/csc_workforce_by_region.jpg';

registerFonts();

// ---------------------------------------------------------------------------
// Empowering People content pages
// ---------------------------------------------------------------------------
export function EmpoweringPeoplePage() {
  return (
    <>
      {/* ── Our Employees ─────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H1 color={COLORS.green}>Our Employees</H1>
          <H3>Employee Wellbeing</H3>
          <Body>
            Columbia Sportswear Company's ("CSC") wellbeing program is designed to support
            employees in every aspect of their life – both inside and outside of the workplace.
            CSC's wellbeing philosophy is centered around five pillars: physical,
            social/emotional, financial, career, and community.
          </Body>
        </Section>

        <SpotlightBox label="U.S. Spotlight: myWellbeing Program" style={{ marginTop: -8 }}>
          <View style={{flexDirection: 'row', gap: 24}}>
            <View style={{flex:1}}>
                <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>
                  In 2023, CSC introduced the myWellbeing reimbursement program for U.S. full-time
                  employees, which provides employees with the opportunity to be reimbursed for their
                  wellness activities. In 2024, CSC reimbursed employees with over $285,000 on
                  approximately 1,300 unique program requests towards various employee wellness
                  activities.
                </Text>
                <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 4 }}>Employees have used their myWellbeing reimbursement for various activities including:</Text>
                <BulletList
                  style={styles.spotlightBoxBulletList}
                  textStyle={{ fontSize: 11, lineHeight: 1.4, color: COLORS.darkGray, marginBottom: 0 }}
                  items={[
                    'Gym memberships',
                    'Running shoes',
                    'Camping gear',
                    'Massage therapy',
                    'Ski lift tickets',
                    'Fitness classes',
                  ]}
                />
              </View>
              <View style={{flex:1}}>
                  <Image src={myWellbeingImg} style={styles.inlineImage} />
              </View>
          </View>
        </SpotlightBox>
        </PageTemplate>

        <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <SpotlightBox label="China Spotlight: Club 1938">
          <View style={{flexDirection: 'row', gap: 24}}>
            <View style={{flex:1}}>
              <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>
                Club 1938 is an outdoor program for our employees based in China, providing a virtual
                hub for sharing tips and ideas to increase knowledge about the outdoors. In 2024, the
                club held two off-site activities.
              </Text>
              <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>The first trip included over 120 employees who embarked on a two-day trip in Tonglu,
                Zhejiang Province. Day one focused on learning wilderness survival skills, while day
                two featured a 10km hike with participants collecting trash along the route.
              </Text>
              <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>The second trip of the year included over 100 employees from three offices who
                participated in a half-day offsite activity. Participants chose from a variety of
                indoor and outdoor activities, ranging from a city walk to nature exploration.
              </Text>
            </View>
            <View style={{flex:1}}>
              <Image src={club1938Img} style={styles.inlineImage} />
            </View>
          </View>
        </SpotlightBox>
        </PageTemplate>

        <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <SpotlightBox label="Vietnam Spotlight: Sports Day">
          <View style={{flexDirection: 'row', gap: 24}}>
            <View style={{flex:1}}>
              <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>
                To promote health awareness, the Vietnam team launched Sports Day, an event where
                employees participated in games designed to foster teamwork, strategy, and perseverance.
                The day was a great success, filled with competition, laughter, and team bonding.
              </Text>
              <Text style={{ fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>
                One employee shared: "When we play together, we grow together — and that's what makes
                us unstoppable!"
              </Text>
            </View>
            <View style={{flex:1}}>
              <Image src={vietnamSportsDayImg} style={styles.inlineImage} />
            </View>
          </View>
        </SpotlightBox>
      </PageTemplate>

      {/* ── Talent Development ─────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H3>Talent Development</H3>
          <Body>
            The mission of CSC's Talent Development team is to promote a culture of continuous
            learning by designing and delivering resources and workshops that enhance skill growth,
            performance impact, and career experience for all employees.
          </Body>

          <H4>Leadership Excellence Acceleration Program (LEAP)</H4>
          <Body>
            LEAP welcomed 45 CSC leaders from 10 countries to participate in a six-month global
            leadership development acceleration program. Designed to elevate the leadership
            capabilities of high-potential people managers, LEAP helps participants become more
            inspired, developed, and connected to drive the future of leadership at Columbia
            Sportswear Company. The 2024 cohort was hosted entirely virtually, with participants
            engaging in workshops, self-led coaching circles, one-on-one sessions with senior
            leaders, and e-learning courses to hone their leadership skills.
          </Body>

          <H4>The Amazing Race Development Program: China</H4>
          <Body>
            Our leadership development program in China, The Amazing Race, is designed to equip
            managers with the skills and insights necessary to thrive in today's complex business
            landscape. Through a blended learning approach, participants deepen their expertise in
            two key areas: Leading Teams and Leading Business. The curriculum combines curated
            LinkedIn Learning courses with immersive sandbox simulation workshops, empowering
            participants to apply their newfound knowledge to real-world business challenges and
            deliver results in dynamic markets.
          </Body>

          <H4>Unlock Your Potential: Europe</H4>
          <Body>
            Since 2022, the teams in Europe have actively promoted internal mobility through the
            Unlock Your Potential program. This initiative features a video and newsletter series
            that highlight employee success stories and showcase career growth across multiple
            roles within the company. This content is designed to encourage employees to pursue
            development and growth opportunities within Columbia.
          </Body>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
          <Section>
          <H4>Retail Virtual Mentorship Event: North America</H4>
          <Body>
            In 2024, the Women's Leadership Initiative Employee Resource Network, in partnership
            with the North America retail team, hosted a virtual mentoring event focused on
            promoting career mobility. The event connected high-performing retail talent with HQ
            business partners to build relationships, increase visibility into career paths, and
            encourage internal mobility by showcasing existing retail talent. Participants
            identified their top career interest and were paired with a female mentor from that
            division. During the event, participants learned about their mentor's career journey,
            gained insight into that area of the business, and asked questions about career
            advancement. Mentors successfully established connections with key talent in the field
            and gained insights into retail's transferable skills, fostering mobility from field
            roles to corporate positions.
          </Body>

          <H4>Manager Fundamentals</H4>
          <Body>
            In 2024, CSC grew the global Manager Fundamentals program, reinforcing our commitment
            to developing strong leaders. The program combines self-guided courses and live
            workshops to build managerial skills, foster community, and promote a culture of
            continuous learning. This year, leaders around the world completed over 1,900 courses,
            resulting in high engagement and satisfaction. The program continues to redefine
            effective management while strengthening our leadership pipeline across the Company.
          </Body>

          <H4>Employee Development Workshops</H4>
          <Body>
            In July 2024, we launched the Employee Development Workshops program, offering monthly
            sessions for employees at all levels focused on building foundational skills. Since its
            inception, the program has covered six skill areas across 12 virtual and in-person
            workshops, with over 200 participants from the U.S., Canada, France, and India.
          </Body>
        </Section>
      </PageTemplate>

      {/* ── ERNs ──────────────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H3>Company Culture & Employee Resource Networks</H3>
          <Body>
            Our ongoing commitment to an inclusive environment for our diverse workforce is
            reflected in our continued efforts to foster a workplace where everyone feels a true
            sense of belonging and inclusion. In 2024, we advanced our work by refining our
            operating model, strengthening our pillars, and further empowering our seven Employee
            Resource Networks (ERN), which are open to all employees. These networks now have
            structured roles, defined terms, and deeper integration with our business strategy,
            ensuring our efforts remain both robust and responsive to a changing world. Each ERN is
            employee-run, supported by an Executive Sponsor, and partnered with a non-profit
            organization. We also reinforce inclusive leadership with sponsorship of senior leader
            training sessions. Our seven CSC Employee Resource Networks are:
          </Body>

        
          <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 8}}>
            {[
              {
                title: 'Vamos Unidos',
                logo: vamosUnidosLogo,
                body: "Since 2022, Vamos Unidos has partnered with Vámonos Outside to support Latino and Hispanic youth getting outside. The organization promotes an inclusive outdoor culture in Central Oregon by offering recreation opportunities for Latino and BIPOC communities. In 2024, Vamos Unidos donated ski kits and sun protection tops to youth participants and helped amplify Vámonos Outside\u2019s impact by hosting the Vámonos Al Cine Film Festival in celebration Earth Month. The festival featured films by Hispanic and Latino filmmakers, highlighting life in the outdoors and the mission of Vámonos Outside. Through this partnership, we\u2019re helping introduce more people to the outdoors and equipping them with the gear they need to enjoy it.",
              },
              {
                title: "Women's Leadership Initiative (WLI)",
                logo: wliLogo,
                body: "WLI offers year-round programming for all employees. Highlights include Women's History Month celebrations, a farmers' market at our Portland HQ featuring local women-owned businesses, inspiring career chats, and support for three community partners: Rose Haven, Dress for Success Oregon, and Days for Girls.",
              },
              {
                title: 'Black Employee Network (BEN) & Friends',
                logo: benLogo,
                body: 'BEN supported several organizations with product donations, including Youth for a Better Future, Orange County Heritage Council, and the Urban League of Greater Pittsburgh. In addition, they hosted Black History Month trivia and a cooking demonstration with chef and author Althea Brown.',
              },
              {
                title: 'PRIDE',
                logo: prideLogo,
                body: 'In 2024, the PRIDE ERN hosted a speaker series called Human Connections. They also celebrated Pride Month by participating in the Portland Pride Parade and donating product to local queer nonprofits supporting LGBTQ+ youth. Additionally, PRIDE collaborated with other ERNs to make a monetary donation to the Cascade AIDS Project.',
              },
              {
                title: 'Coalition of Asian & Pacific Islander Employees (CAPE)',
                logo: capeLogo,
                body: 'CAPE celebrated Lunar New Year with a festive event featuring dragon and lion dances, and honored AAPI Heritage Month with Tai Chi, calligraphy, and origami activities. The team also gave back to the community by volunteering with the Immigrant and Refugee Community Organization, with participation from employees at our Rivergate Distribution Center.',
              },
              {
                title: 'BRAVO (Bringing Representation and Valor to the Outdoors)',
                logo: bravoLogo,
                body: 'BRAVO supported veterans in our community through several initiatives, including participation in a Memorial Day Ruck March and contributions to the Minnesota Assistance Council for Veterans, the Hillsboro Stand Down, and Toys for Tots.',
              },
              {
                title: 'Our Trail',
                logo: ourTrailLogo,
                body: 'Our Trail focused on allyship and supporting all ERNs through collaborative events. The group also hosted a virtual Lunch and Learn titled "How to Be an Ally," which was open to all employees.',
              },
            ].map((ern) => (
              <View key={ern.title} style={styles.ernCard}>
                {ern.logo ? <Image src={ern.logo} style={styles.ernLogo} /> : null}
                <Text style={styles.ernTitle}>{ern.title}</Text>
                <Text style={styles.ernBody}>{ern.body}</Text>
              </View>
            ))}
          </View>
        </Section>
      </PageTemplate>

      {/* ── Community spotlights / workforce / supply chain ────────────────── */}
      <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H3>Community Group Spotlights</H3>

          <H4>Green Teams</H4>
          <Body>
            Employee-run Green Teams support sustainability initiatives across our offices by
            hosting events and turning employee ideas into action. In 2024, the prAna and Geneva
            Green Teams each organized clothing swap events. prAna also hosted a natural dye
            workshop and a seed bomb-making activity.
          </Body>

          <H4>Mountain Hardwear I.D.E.A. (Inclusion, Diversity, Equity and Action)</H4>
          <Body>
            Mountain Hardwear's I.D.E.A. group plans diversity and inclusion activities for
            employees and amplifies corporate diversity and inclusion initiatives. In 2024, the
            group supported a Giving Tuesday campaign that benefitted five nonprofits in the Bay
            Area.
          </Body>

          <H4>Inclusion at our Cambrai, France Distribution Center</H4>
          <Body>
            Our Cambrai, France Distribution Center team was honored by the Cambrésis Agglomeration
            Community and the sub-prefect for fostering an inclusive workplace. The team remains
            committed to creating an environment where employees feel welcome, valued, and
            supported in their roles. This annual recognition celebrates everyday business
            practices that promote a more inclusive society in France and a more connected global
            community.
          </Body>
        </Section>

        <Section>
          <H3>Our Workforce at a Glance</H3>
          <Body>
            This data is collected as part of our routine reporting process and is provided
            voluntarily by employees, who value the opportunity to share this information. The
            company does not maintain specific targets related to these categories.
          </Body>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
          <Section>
          <View>
            <Text style={styles.pillHeading}>Workforce by Level</Text>
            <Image src={workforceLevelGenderImg} style={styles.chartImage} />
            <Image src={workforceLevelEthnicityImg} style={styles.chartImage} />
          </View>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
          <Section>
          <View>
            <Text style={styles.pillHeading}>Workforce by Role</Text>
            <Image src={workforceRoleGenderImg} style={styles.chartImage} />
            <Image src={workforceRoleEthnicityImg} style={styles.chartImage} />
          </View>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
          <Section>
          <View>
            <Text style={styles.pillHeading}>Workforce by Region</Text>
            <Image src={workforceRegionImg} style={styles.chartImage} />
          </View>
          <Small>* U.S. only</Small>
          <Small>** Includes individuals who selected "undisclosed" as well as those who left the question blank</Small>
          <Small>Charts available in the online report at columbia.com/corporate-responsibility/impact/2024</Small>
        </Section>
      </PageTemplate>
      <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H2 color={COLORS.green}>Our Supply Chain</H2>
          <H3>CSC Clean Drinking Water Program</H3>
          <Text style={styles.body}>
            To combat the global water crisis and improve access to clean drinking water, we
            continue our partnership with <Link href="https://www.columbia.com/planet_water_foundation.html">Planet Water Foundation</Link>, a U.S. nonprofit, to build
            AquaTowers (water filtration systems) in areas where CSC products are made.
          </Text>
          <Body>The Planet Water AquaTowers provide numerous benefits:</Body>
          <BulletList
            items={[
              'Access to safe drinking water for communities where our products are made',
              'Clean water for up to 1,800 people per tower, per day',
              'Hygiene education for students at each tower installation site',
              'Team-building opportunities with our factory vendors',
            ]}
          />
          <Body>
            Our goal: Provide clean drinking water to 100,000 people daily by 2028.
          </Body>
          <Body>
            Progress to date: As of 2024, we've reached over 80% of our goal as we work towards
            providing access to clean water for 100,000 people per day.
          </Body>
          <Body>
            In 2024, we proudly partnered with Planet Water Foundation and our factory vendors to
            build ten AquaTowers in manufacturing communities. One tower was a joint initiative
            between Columbia and seven of our licensees, underscoring our shared commitment to the
            regions where we produce goods.
          </Body>
          <Body>
            Two additional AquaTowers were built in partnership with vendors that produce prAna
            products – one in Vietnam and one in Guatemala.
          </Body>
          <Body>
            In recognition of these efforts, Columbia Sportswear Company was honored in 2024 by
            AmCham Vietnam for its positive impact through clean water initiatives.
          </Body>
          <Blockquote
            quote={'"We are truly grateful for the AquaTower and Hygiene Education Program that Columbia Sportswear Company and Zuntex provided to our school. This project will benefit our children and the entire community."'}
            attribution="— Yosselin Aceituno, Teacher, Escuela Oficial Rural Mixta de Los Rios, San Vicente Pacay, Guatemala"
          />
          <View style={styles.twoImageRow}>
            <Image src={aquaTowerImg} style={styles.bigImage} />
            <Image src={raiseAglassImg} style={styles.bigImage} />
          </View>
          <Text style={styles.body}>
            <Link href="https://www.prana.com/life/sustainability/planet-water-foundation.html">Learn more about our Planet Water Foundation partnership.</Link>
          </Text>
        </Section>
      </PageTemplate>

      {/* ── Communities ───────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H2 color={COLORS.green}>Our Communities</H2>
          <H3>Employees Get Involved</H3>
          <Body>
            At CSC, we prioritize making time and space for team members to stay involved in their
            communities. That's why our Volunteer Time-off Policy provides eligible employees with
            16 hours of paid time off each year to support causes that matter most to them.
          </Body>
          <Body>
            Employees consistently share that this policy allows them to use their time and energy
            to make a meaningful impact, while also feeling supported by the business. In 2024
            alone, team members logged over 6,100 volunteer hours. And, all full-time, global
            employees can receive up to $1,000 per year of matched funds for nonprofit donations of
            their choosing.
          </Body>
          <View style={styles.twoImageRow}>
            <Image src={volunteer2Img} style={styles.bigImage} />
            <Image src={volunteerImg} style={styles.bigImage} />
          </View>
        </Section>

        </PageTemplate>
        <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
        <Section>
          <H3>Donations</H3>

          <H4>Employee Store Community Cares Program</H4>
          <Body>
            We partner with local nonprofits by inviting their supporters to shop at our exclusive
            Employee Stores. This special access allows them to purchase products from our family
            of brands – Columbia Sportswear, SOREL, Mountain Hardwear, and prAna – at a discounted
            price. Ten percent of the total net sales from these visits is then donated back to
            the nonprofit.
          </Body>

          <H4>Tough Mother Funder</H4>
          <Body>
            Through our Tough Mother Funder, the Columbia brand invites U.S. customers to donate to
            selected nonprofit organizations at the register. In 2024, the program supported four
            different organizations, including the Land Trust Alliance, the National Park
            Foundation, and Captains for Clean Water. In response to Hurricane Helene in September
            2024, the featured organization shifted to the Center for Disaster Philanthropy to aid
            in relief efforts.
          </Body>

          <H4>Disaster Relief</H4>
          <Body>
            Columbia is committed to supporting disaster relief efforts in the regions where we
            operate and sell our products. In 2024, we provided aid in response to several
            disasters, including Hurricane Helene in the Southeast United States and severe
            flooding in Eastern Spain.
          </Body>

          <H5>Southeast United States Hurricane</H5>
          <BulletList
            items={[
              'Monetary donation to the Center for Disaster Philanthropy.',
              "Consumer donations made in all Columbia U.S. retail stores supported the Center for Disaster Philanthropy's Helene Fund for designated period.",
              'Product Donations provided to local organizations supporting disaster relief.',
              'Employees were encouraged to donate through the CSC Community Impact Portal to receive a company match.',
            ]}
          />

          <H5>Eastern Spain Floods</H5>
          <BulletList
            items={[
              'Monetary donation to Cruz Roja Española.',
              'Employees were encouraged to donate through the CSC Community Impact Portal to receive a company match.',
            ]}
          />
        </Section>

        <Section>
          <H3>Community Impact</H3>

          <H4>Collegiate Outdoor Recreation Program</H4>
          <Body>
            Columbia, prAna, and Mountain Hardwear support the Collegiate Outdoor Recreation
            Program, helping over 71,000 students access the clothing and equipment they need to
            safely and comfortably enjoy the outdoors based on the most recent available 2023 data
            year. Many college students face barriers to accessing high-quality outdoor gear due to
            cost, limited storage space, or accessibility. By donating clothing and equipment to
            outdoor programs at 16 universities in the U.S., our brands help students experience
            everything from remote mountain climbs to urban park adventures.
          </Body>
          <Body>
            College campus outdoor recreation offices organize activities like hiking, skiing, and
            climbing, introducing students to pursuits they may have otherwise missed. These
            programs also provide work experience, access to top-of-the-line gear, and
            opportunities to form lasting friendships.
          </Body>
          <Body>
            Sustainability is a key focus of the program. Students rent gear to ensure it's reused
            over time and learn how to care for and repair it, extending its lifespan and reducing
            waste. The goal is to make outdoor activities more accessible and instill a lifelong
            appreciation for nature.
          </Body>

          <H4>Earth Day Campaign</H4>
          <Text style={styles.body}>
            We believe protecting the planet starts in our own backyard. For Columbia's 2024 Earth
            Day campaign, we <Link href="https://oceanconservancy.org/about/partner/columbia-sportswear/">partnered</Link> with <Link href="https://oceanconservancy.org">Ocean Conservancy</Link>, an organization dedicated to
            evidence-based solutions for a healthier ocean and the communities and wildlife that
            depend on it. To support the fight for trash-free tides and trails, we encouraged
            consumers to download <Link href="https://oceanconservancy.org/trash-free-seas/international-coastal-cleanup/clean-swell-app/">Ocean Conservancy's Clean Swell®</Link> trash collection app.
          </Text>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Empowering People" accentColor={COLORS.green}>
          <Section>

          <H4>Columbia x National Park Foundation Partnership</H4>
          <Body>
            The Columbia brand has partnered with the National Park Foundation since 2013, and
            began supporting the Open OutDoors for Kids program in 2019. This program helps
            connect students to national parks through field trips, classroom activities, and
            hands-on educational experiences.
          </Body>

          <H4>Columbia Canada x Trans Canada Trail Partnership</H4>
          <Body>
            The Columbia brand in Canada has a multi-year partnership with Trans Canada Trail, the
            longest trail network in the world. As an official sponsor, Columbia supports the Trail
            Care program, which funds projects to repair, improve, and increase accessibility along
            the trail. In 2024, we supported Trans Canada Trail in three big ways:
          </Body>
          <BulletList
            items={[
              'Columbia Staff Clean Up: In May 2024, Columbia Canada employees used paid volunteer hours to participate in a trail clean-up near the Columbia Canada office, helping to remove trash and maintain the trail.',
              'Tough Mother Funder: Customers at our Canadian Retail Outlet Stores were invited to donate to the Trans Canada Trail at checkout through the Tough Mother Funder program, supporting trail maintenance efforts.',
              'Product Donations: The Columbia brand provided product to Trans Canada Trail staff, board members, and influencers. Additional product was also donated for use in Trans Canada Trail events and activations.',
            ]}
          />

          <H4>Mountain Hardwear x Leave No Trace Partnership</H4>
          <Text style={styles.body}>
            Mountain Hardwear was named the <Link href="https://www.mountainhardwear.com/12-20_PressCenter_LNT.html">2024 Leave No Trace Corporate Partner of the Year</Link>,
            recognizing the brand's commitment to environmental stewardship and sustainable outdoor
            practices. Throughout the year, the company actively promoted Leave No Trace principles
            and engaged in meaningful conservation efforts.
          </Text>
          <Body>
            One highlight of the year was a volunteer initiative in the Bay Area, where employees
            contributed over 100 hours to environmental projects. Mountain Hardwear also helped
            expand the Leave No Trace ambassador program, with athletes Shayna Unger and Scott
            Lehmann joining as ambassadors to amplify the message of responsible outdoor practices.
          </Body>
          <Body>
            In addition, the company co-presented at outdoor festivals, educating thousands on
            Leave No Trace principles, and proudly sponsored the Leave No Trace Global Summit in
            celebration of the organization's 30th anniversary.
          </Body>
          <View style={styles.twoImageRow}>
            <Image src={leaveNoTraceImg} style={styles.bigImage} />
            <Image src={leaveNoTrace2Img} style={styles.bigImage} />
          </View>

          <H4>Mountain Hardwear x American Mountain Guides Association (AMGA)</H4>
          <Body>
            In partnership with the American Mountain Guides Association (AMGA), Mountain Hardwear
            offered 11 scholarships in 2024 for the annual LGBTQIA2S+ Single Pitch Instructor
            Course. These courses are designed to meet the specific needs of participants,
            providing the training and support required to succeed in climbing instruction. By
            prioritizing inclusivity and representation, Mountain Hardwear and AMGA aim to
            encourage more people from diverse backgrounds to engage in climbing and pursue
            leadership roles within the sport.
          </Body>
          <Text style={styles.body }>
            Over the past five years, the program has helped certify 14 LGBTQIA2S+ individuals as
            Single Pitch Instructors, the AMGA's entry-level climbing guide certification. These
            certified instructors now serve as leaders and role models, helping diversify the
            climbing world and inspire others in the community. <Link href="https://www.mountainhardwear.com/affinity-programs.html">Learn more here.</Link>
          </Text>
          <Body>
            To learn more about each brand's community impact initiatives and nonprofit partnerships, check out their websites:
          </Body>
          <View style={{ marginTop: 4 }}>
            <Text style={styles.body}><Link href="https://www.columbia.com/corporate-responsibility/">Columbia</Link></Text>
            <Text style={styles.body}><Link href="https://www.sorel.com/ocean-conservancy.html">SOREL</Link></Text>
            <Text style={styles.body}><Link href="https://www.mountainhardwear.com/nonprofit-partners.html">Mountain Hardwear</Link></Text>
            <Text style={styles.body}><Link href="https://www.prana.com/sustainability/membership-and-cause-partners.html">prAna</Link></Text>
          </View>
        </Section>
      </PageTemplate>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section composite — cover + content (for use in the full assembled report)
// ---------------------------------------------------------------------------
export function EmpoweringPeopleSection() {
  return (
    <>
      <SectionCover
        bgImage={empoweringPeopleHero}
        accentColor={COLORS.green}
        title="Empowering People"
        subtitle="Supporting and uplifting the communities where we live, work, and play is at the heart
            of what we do. Our key areas of focus include our employees, our supply chain, and our
            communities."
        quote="At Columbia, we believe that empowering our employees goes hand in hand with uplifting the communities where we live and work."
        attribution="— Richelle Luther, Corporate Affairs & Chief HR Officer"
      />
      <EmpoweringPeoplePage />
    </>
  );
}

// ---------------------------------------------------------------------------
// Standalone Document (used by the generate script)
// ---------------------------------------------------------------------------
export default function EmpoweringPeoplePDF({ pageOffset = 0, totalDocPages = null } = {}) {
  return (
    <PageOffsetContext.Provider value={{ pageOffset, totalDocPages }}>
      <Document
        title="2024 CSC Impact Report — Empowering People"
        author="Columbia Sportswear Company"
      >
        <EmpoweringPeopleSection />
      </Document>
    </PageOffsetContext.Provider>
  );
}
